




import { getConfig } from '@superbet-group/web.lib.config';
import { useVuexStore } from '@superbet-group/web.lib.vue-utilities';
import { BetSlipPurchaseType } from '@superbet-group/web.types';
import {
    defineComponent,
    onMounted,
} from '@vue/composition-api';
import { ActionContext } from 'vuex';

import {
    TranslationKey,
    Paths,
    PlayerAlternativeChannelError,
    AnalyticsSource,
    AnalyticsStatus,
    AnalyticsActions,
} from '@accountFederation/modules/accountRecovery/constants';
import {
    AccountRecoveryToken,
    ModalNotice,
} from '@accountFederation/modules/accountRecovery/types';
import { RouteName } from '@app/router/constants/RouteName';
import { authenticationService } from '@core/services/authentication/AuthenticationService';
import PushNotificationsService from '@core/services/pushNotifications/PushNotificationsService';
import { UserService } from '@core/services/user/UserService';
import router from '@src/app/router';
import { IState } from '@store/index';
import {
    AuthenticationStore,
    IAuthenticationState,
} from '@store/modules/data/authentication/authenticationIndex';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import modalStore from '@store/modules/ui/shared/modal/modalIndex';
import { BetSlipModule } from '@store/modules/ui/sportOffer/betslip';
import messagesIndex from '@store/modules/ui/user/messages/messagesIndex';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';

export default defineComponent({
    name: 'AccountLockedRecoveryPage',
    setup() {
        const modalModule = useVuexStore<typeof modalStore>('ui/modal');
        const setModal = modalModule.action('setModal');
        const authenticationModule = useVuexStore<AuthenticationStore>('data/authentication');
        const setSessionState = authenticationModule.action('setSessionState');
        const clearSessionState = authenticationModule.action('clearSessionState');
        const setSessionStartTime = authenticationModule.action('setSessionStartTime');
        const sportBetSlipStore = useVuexStore<BetSlipModule>('ui/sportOffer/betSlip');
        const messagesModule = useVuexStore<typeof messagesIndex>('ui/user/messages');
        const showPendingMessages = messagesModule.action('showPendingMessages');
        const goToLanding = () => {
            router.replace({
                name: RouteName.App.Home,
            });
        };

        const displayModal = (modalConfig: ModalNotice) => {
            setModal({
                code: modalCodes.accountLockNotice,
                isClosable: false,
                ...modalConfig,
            });
        };

        const displayTokenExpiredErrorModal = () => {
            displayModal({
                title: TranslationKey.UnlockedErrorModalTitleNotice,
                data: {
                    illustrationPath: Paths.NoticeEmail,
                    translateKey: TranslationKey.UnlockedErrorModalNotice,
                    closeBtnKey: TranslationKey.CloseBtnResendLinkKey,
                    closeHandler: () => {
                        setModal({
                            code: modalCodes.accountLocked,
                            data: {
                                source: AnalyticsSource.ExpiredLink,
                                eventAction: AnalyticsActions.ScreenLoad,
                            },
                        });
                    },
                },
            });
            AnalyticsSDK.getInstance().log('Login_Account_Locked', {
                status: AnalyticsStatus.Expired,
                source: AnalyticsSource.FailedAttempts,
                eventAction: AnalyticsActions.Message,
            });
        };

        const displaySuccessModal = () => {
            displayModal({
                title: TranslationKey.UnlockedSuccessModalTitleNotice,
                data: {
                    illustrationPath: Paths.AccountSuccess,
                    translateKey: TranslationKey.UnlockSuccessModalNotice,
                },
            });
            AnalyticsSDK.getInstance().log('Login_Account_Locked', {
                status: AnalyticsStatus.Success,
                source: AnalyticsSource.FailedAttempts,
                eventAction: AnalyticsActions.Message,
            });
        };

        const displayErrorInvalidTokenModal = () => {
            displayModal({
                title: TranslationKey.LoginErrorModalTitle,
                data: {
                    illustrationPath: Paths.NoticeEmail,
                    translateKey: TranslationKey.LoginErrorModalNotice,
                    closeBtnKey: TranslationKey.CloseBtnLoginErrorModal,
                },
            });
            AnalyticsSDK.getInstance().log('Login_Account_Locked', {
                status: AnalyticsStatus.Error,
                source: AnalyticsSource.FailedAttempts,
                eventAction: AnalyticsActions.Message,
            });
        };

        const onLoginPlayerAlternativeChannel = async (context: ActionContext<IAuthenticationState, IState>, token: AccountRecoveryToken) => {
            goToLanding();
            await clearSessionState(context);
            const alternativeChannelResponse = await authenticationService.loginPlayerAlternativeChannelRequest(token);
            if (alternativeChannelResponse.error) {
                return alternativeChannelResponse.errorCode === PlayerAlternativeChannelError.PlayerTokenExpired ? displayTokenExpiredErrorModal() : displayErrorInvalidTokenModal();
            }
            const user = alternativeChannelResponse.data;

            if (user) {
                user.balance = await UserService.getInstance().getPlayerBalance();
                const details = await UserService.getInstance().getUserDetails();
                Object.assign(user, details);
                user.welcomeBonus = getConfig().countrySpecific.user.bonus.isWelcomeBonusShown;
                setSessionStartTime(Date.now());
                setSessionState(user);
                const setPurchaseType = sportBetSlipStore.action('setPurchaseType');
                setPurchaseType({ root: true, type: BetSlipPurchaseType.online });
                if (getConfig().user.arePushNotificationsEnabled) {
                    PushNotificationsService.getInstance().connect(`${user.userId}`, user.sessionId);
                    showPendingMessages({ isLogin: true, root: true });
                }
                displaySuccessModal();
            }
        };

        onMounted((context: ActionContext<IAuthenticationState, IState>) => {
            onLoginPlayerAlternativeChannel(context, router.currentRoute.query.passwordToken);
        });
    },
});
